import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { EnterpriseLicenseActions } from './enterprise-license.actions';
import { toasty } from '../../global/operators/ngxs-rxjs-toast';
import { EnterpriseLicense } from 'src/app/predictor-enterprise-license/shared/interface/Enterprise-License';
import { EnterpriseLicenseService } from 'src/app/predictor/services/enterprise-license.service';
import { Observable } from 'rxjs';
import { LicenseStudyAreaServiceService } from 'src/app/predictor/services/license-study-area-service.service';
import { LicenseTierDropdown } from 'src/app/predictor-enterprise-license/shared/interface/license-tiers';


export interface EnterpriseLicenseStateModel {
  licenses: EnterpriseLicense[];
  licenseTiers: LicenseTierDropdown[];
  continuationTokens: string[];
  totalRecords: number;
  loadingLicenses: boolean;
  pageIndex: number;
  pageSize: number;
}

const defaults: EnterpriseLicenseStateModel = {
  licenses: [],
  licenseTiers:[],
  continuationTokens: [''],
  totalRecords: 0,
  loadingLicenses: false,
  pageIndex: 0,
  pageSize: 10
};

@State<EnterpriseLicenseStateModel>({
  name: 'EnterpriseLicense',
  defaults
})
@Injectable()
export class EnterpriseLicenseState {

  constructor(private readonly licenseService: EnterpriseLicenseService, private readonly StudyAreaServiceService : LicenseStudyAreaServiceService) {}

  // Selectors
  @Selector()
  static licenses(state: EnterpriseLicenseStateModel) {
    return state.licenses;
  }

  @Selector()
  static loading(state: EnterpriseLicenseStateModel) {
    return state.loadingLicenses;
  }

  @Selector()
  static totalRecords(state: EnterpriseLicenseStateModel) {
    return state.totalRecords;
  }

  @Selector()
static licenseTiers(state: EnterpriseLicenseStateModel): LicenseTierDropdown[] {
  return Array.isArray((state.licenseTiers as any)?.$values)
    ? (state.licenseTiers as any).$values
    : [];
}

  // Actions
  @Action(EnterpriseLicenseActions.ChangePageIndex)
  changePageIndex(ctx: StateContext<EnterpriseLicenseStateModel>, { pageIndex }: EnterpriseLicenseActions.ChangePageIndex) {
    ctx.patchState({ pageIndex });
  }

  @Action(EnterpriseLicenseActions.ChangePageSize)
  changePageSize(ctx: StateContext<EnterpriseLicenseStateModel>, { pageSize }: EnterpriseLicenseActions.ChangePageSize) {
    ctx.patchState({ pageSize, pageIndex: 0, continuationTokens: [''] });
  }

  @Action(EnterpriseLicenseActions.GetLicenses)
  getLicenses(ctx: StateContext<EnterpriseLicenseStateModel>) {
    ctx.patchState({ loadingLicenses: true });
    const { continuationTokens, pageIndex, pageSize } = ctx.getState();

    return this.licenseService.getAll().pipe( // 🔹 Replace with paged API if available
      tap((result) => {
        // If API supports continuation tokens, manage them here
        if (continuationTokens.length <= pageIndex + 1) {
          continuationTokens.push(''); // Replace '' with result.nextContinuationToken if available
        }

        ctx.patchState({
          licenses: result,
          totalRecords: result.length,
          loadingLicenses: false
        });
      })
    );
  }

@Action(EnterpriseLicenseActions.CreateLicense)
createLicense(ctx: StateContext<EnterpriseLicenseStateModel>, { license }: EnterpriseLicenseActions.CreateLicense) {
  return this.licenseService.create(license).pipe(
    toasty(
      { success: 'License created successfully', error: 'Failed to create license' },
      ctx.dispatch
    ),
    tap((created) => {
      ctx.setState(patch({
        licenses: [...ctx.getState().licenses, created]
      }));
    })
  );
}


@Action(EnterpriseLicenseActions.UpdateLicense)
updateLicense(ctx: StateContext<EnterpriseLicenseStateModel>, { id, license, toastMessages }: EnterpriseLicenseActions.UpdateLicense) {
  return this.licenseService.update(id, license).pipe(
    toasty(toastMessages ?? {}, ctx.dispatch), // ✅ default to {}
    tap((updated) => {
      ctx.setState(
        patch({
          licenses: updateItem<EnterpriseLicense>(l => l.id === id, updated)
        })
      );
    })
  );
}

 @Action(EnterpriseLicenseActions.DeleteLicense)
deleteLicense(ctx: StateContext<EnterpriseLicenseStateModel>, { id, toastMessages }: EnterpriseLicenseActions.DeleteLicense) {
  return this.licenseService.delete(id).pipe(
    toasty(toastMessages ?? {}, ctx.dispatch), // ✅ default to {}
    tap(() => {
      ctx.setState(
        patch({
          licenses: removeItem<EnterpriseLicense>(l => l.id === id)
        })
      );
    })
  );
}



@Action(EnterpriseLicenseActions.CheckUniqueStudyAreaName)
checkUniqueStudyAreaName(
  ctx: StateContext<boolean>,
  action: EnterpriseLicenseActions.CheckUniqueStudyAreaName
): Observable<boolean> {
  const { studyAreaName, licenseNumber } = action;

  return this.StudyAreaServiceService.isUniqueStudyAreaNameForGivenLicense(studyAreaName, licenseNumber).pipe(
    tap((isUnique) => {
      console.log('Is unique study area:', isUnique);
      return isUnique;
    })
  );
}

@Action(EnterpriseLicenseActions.GetLicenseTiers)
getLicenseTiers(ctx: StateContext<EnterpriseLicenseStateModel>) {
  return this.licenseService.getLicenseTiers().pipe( // 🔹 make sure this service method exists
    tap((tiers: LicenseTierDropdown[]) => {
      ctx.patchState({
        licenseTiers: tiers
      });
    })
  );
}
}

